import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorText } from 'client/components';
import { PasswordInput } from 'client/components';
import { RoutesEnum } from 'client/enums/routes.enum';

import { validationSchema } from '../../schemas/login.schema';
import { LoginParamsType } from '../../types/login-params.type';

type LoginFormPropsType = {
  loading: boolean;
  onLoginSubmit: (params: LoginParamsType) => void;
};

export const LoginForm = (props: LoginFormPropsType): React.ReactElement => {
  const { loading, onLoginSubmit: login } = props;

  const {
    register: validationRegister,
    handleSubmit: validationSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const handleSubmit = (data: LoginParamsType) => {
    login({
      email: data.email,
      password: data.password,
      remember: !!data.remember,
    });
  };

  return (
    <>
      <Typography component="h1" variant="h5">
        Sign in
      </Typography>
      <Box
        noValidate
        component="form"
        onSubmit={validationSubmit(handleSubmit)}
        sx={{ mt: 1 }}
        width={1}
      >
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          autoComplete="email"
          autoFocus
          {...validationRegister('email')}
          error={!!errors.email}
        />
        <ErrorText variant="inherit">{errors.email?.message}</ErrorText>
        <Controller
          name="password"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <PasswordInput
              {...rest}
              inputRef={ref}
              margin="normal"
              required
              fullWidth
              variant="outlined"
              label="Password"
              validationError={!!errors.password}
            />
          )}
        />
        <ErrorText variant="inherit">{errors.password?.message}</ErrorText>
        <FormControlLabel
          control={
            <Controller
              control={control}
              name="remember"
              defaultValue={false}
              render={({ field: { onChange, ref, ...rest } }) => (
                <Checkbox
                  {...rest}
                  inputRef={ref}
                  onChange={(e) => onChange(e.target.checked)}
                  name="remember"
                  color="primary"
                />
              )}
            />
          }
          label="Remember me"
        />
        <LoadingButton
          type="submit"
          loading={loading}
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          Sign In
        </LoadingButton>
        <Grid container>
          <Grid item xs>
            <Link
              component={RouterLink}
              to={RoutesEnum.ForgotPassword}
              variant="body2"
            >
              Forgot password?
            </Link>
          </Grid>
          <Grid item>
            <Link
              component={RouterLink}
              to={RoutesEnum.Register}
              variant="body2"
            >
              {"Don't have an account? Sign Up"}
            </Link>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default LoginForm;
